import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { LoadingIndicator, NewModal, Spinner } from 'lib/components';
import { Tag, Button, Typography } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { Flex } from '../styles/layout';
import { useGetAllTags } from 'lib/api/videoTags/useGetAllTags';
import { useCreateTagMutation } from 'lib/api/videoTags/useCreateTagMutation';
import { TAG_LIMIT } from 'lib/const/VideoTags';
import { Formik, useFormikContext, ErrorMessage, Form } from 'formik';
import { TagType } from 'react-covideo-common/dist/components/Tag/types';
import {
  FormikDropdownField,
  FormikDropdownOption,
} from '../formik/FormikDropdown';
import { VideoTag } from 'lib/api/videoTags/types';

type FormValues = {
  tags: TagType[];
  tagSelect: string | number | null;
};

type TagsFormProps = {
  buttonTitle: string;
  isSaving?: boolean;
  t: any;
  tagsData: VideoTag[] | undefined;
  isLoading: boolean;
};

const ErrorMsg = styled(Typography)`
  color: ${({ theme }) => theme.colors.danger[100]};
  font-size: 14px;
  padding: 0.1rem;
`;

const TagsFormContent = ({
  buttonTitle,
  isSaving = false,
  t,
  tagsData,
  isLoading,
}: TagsFormProps) => {
  const {
    values,
    setFieldValue,
    dirty,
    errors,
    setFieldError,
    submitForm,
    setFieldTouched,
  } = useFormikContext<FormValues>();

  const { mutateAsync: createTag, isLoading: isCreatingTag } =
    useCreateTagMutation();

  const isSearchDisabled = isCreatingTag || isLoading;

  const availableTags = React.useMemo(() => {
    if (!tagsData) return [];

    return tagsData
      .filter(tag => !values.tags.some(t => t.value === tag.tagId))
      .map(tag => ({
        label: tag.tag,
        value: tag.tagId,
      }));
  }, [tagsData, values.tags]);

  const addTag = React.useCallback(
    (tag: TagType) => {
      setFieldValue('tags', [...values.tags, tag]);
    },
    [values.tags, setFieldValue]
  );

  const removeTag = React.useCallback(
    (index: number) => {
      const next = [...values.tags];
      next.splice(index, 1);
      setFieldValue('tags', next);
    },
    [values.tags, setFieldValue]
  );

  const toggleTag = React.useCallback(
    (tag: TagType) => {
      const existsIndex = values.tags.findIndex(t => t.value === tag.value);

      if (existsIndex >= 0) removeTag(existsIndex);
      else addTag(tag);
    },
    [values.tags, addTag, removeTag]
  );

  const handleSelect = async (option: FormikDropdownOption) => {
    if (!option?.value) return;

    const label = option.label?.toString() ?? '';

    if (label.length > TAG_LIMIT) {
      setFieldTouched('tags', true, false);
      setFieldError(
        'tags',
        t('Tag can not contain more than {{limit}} characters.', {
          limit: TAG_LIMIT,
        })
      );
      return;
    }

    setFieldError('tags', undefined);

    if (option.__isNew__) {
      const newTag = await createTag({ tag: label });

      addTag({
        label: newTag.tag,
        value: newTag.tagId,
      });
    } else {
      toggleTag(option as TagType);
    }

    setFieldValue('tagSelect', null);
  };

  const isDisabled = !!errors.tags || isSearchDisabled || !dirty;

  return (
    <Form>
      <Flex width='100%'>
        <Flex>
          <Flex gap='5px'>
            <FormikDropdownField
              name='tagSelect'
              options={availableTags}
              creatable
              isSearchable
              disabled={isSearchDisabled}
              placeholder={
                isCreatingTag ? t('Creating...') : t('Add tag(s)...')
              }
              onChange={handleSelect}
            />

            <ErrorMessage name='tags' component={ErrorMsg} />
          </Flex>

          <Flex
            flexDirection='row'
            gap='5px'
            flexWrap='wrap'
            margin='10px 0'
            width='592px'
          >
            {values.tags.map((tag, index) => (
              <Tag
                key={`${tag.value}-${index}`}
                tag={tag}
                onRemove={() => removeTag(index)}
              />
            ))}
          </Flex>
        </Flex>

        <Flex flexDirection='row' justifyContent='flex-end' margin='auto 0 0 0'>
          <Button
            text={buttonTitle}
            disabled={isDisabled}
            loading={isSaving}
            icon={isSaving ? <Spinner /> : undefined}
            onClick={submitForm}
          />
        </Flex>
      </Flex>
    </Form>
  );
};

export const ModalVideoTags = ({
  videoTags = [],
  handleModalClose,
  onSubmit,
  title,
  buttonTitle,
  isSaving = false,
}: any) => {
  const themes = useTheme();
  const { t } = useTranslation();
  const { data: tagsData, isLoading } = useGetAllTags({
    start: 0,
    limit: 50,
    search: '',
    all: true,
  });

  return (
    <NewModal
      closeModal={handleModalClose}
      headerText={title}
      style={{
        content: {
          minHeight: '200px',
        },
      }}
    >
      {!isLoading ? (
        <Formik<FormValues>
          initialValues={{
            tags: videoTags || [],
            tagSelect: null,
          }}
          onSubmit={values => onSubmit(values.tags)}
        >
          <TagsFormContent
            buttonTitle={buttonTitle}
            isSaving={isSaving}
            t={t}
            tagsData={tagsData}
            isLoading={isLoading}
          />
        </Formik>
      ) : (
        <Flex
          width='100%'
          height='100%'
          alignItems='center'
          justifyContent='center'
        >
          <LoadingIndicator
            isLoading={true}
            color={themes.colors.primary[100]}
            text=''
          />
        </Flex>
      )}
    </NewModal>
  );
};
